/**
 * Debug helper utility for conditional console logging
 * Uses RESPONZA_DEBUG environment variable to enable/disable debug output
 */

/**
 * Checks if debug mode is enabled
 * @returns {boolean} true if RESPONZA_DEBUG is set to 'true'
 */
export const isDebugEnabled = () => {
  // Check if we're in a Rails environment with window.chatwootConfig
  if (typeof window !== 'undefined' && window.chatwootConfig?.RESPONZA_DEBUG) {
    return window.chatwootConfig.RESPONZA_DEBUG === 'true';
  }
  
  // Check process.env for Node environments
  if (typeof process !== 'undefined' && process.env?.RESPONZA_DEBUG) {
    return process.env.RESPONZA_DEBUG === 'true';
  }
  
  // Check meta tag (Rails often passes env vars this way)
  if (typeof document !== 'undefined') {
    const metaTag = document.querySelector('meta[name="responza-debug"]');
    if (metaTag) {
      return metaTag.getAttribute('content') === 'true';
    }
  }
  
  return false;
};

/**
 * Debug log function that only outputs when RESPONZA_DEBUG is enabled
 * @param {...any} args - Arguments to pass to console.log
 */
export const debug = (...args) => {
  if (isDebugEnabled()) {
    console.log('[RESPONZA DEBUG]', ...args);
  }
};

/**
 * Debug error function that only outputs when RESPONZA_DEBUG is enabled
 * @param {...any} args - Arguments to pass to console.error
 */
export const debugError = (...args) => {
  if (isDebugEnabled()) {
    console.error('[RESPONZA DEBUG ERROR]', ...args);
  }
};

/**
 * Debug warn function that only outputs when RESPONZA_DEBUG is enabled
 * @param {...any} args - Arguments to pass to console.warn
 */
export const debugWarn = (...args) => {
  if (isDebugEnabled()) {
    console.warn('[RESPONZA DEBUG WARN]', ...args);
  }
};

/**
 * Debug info function that only outputs when RESPONZA_DEBUG is enabled
 * @param {...any} args - Arguments to pass to console.info
 */
export const debugInfo = (...args) => {
  if (isDebugEnabled()) {
    console.info('[RESPONZA DEBUG INFO]', ...args);
  }
};

/**
 * Debug table function that only outputs when RESPONZA_DEBUG is enabled
 * @param {any} data - Data to display in table format
 * @param {Array<string>} columns - Optional columns to display
 */
export const debugTable = (data, columns) => {
  if (isDebugEnabled()) {
    console.log('[RESPONZA DEBUG TABLE]');
    console.table(data, columns);
  }
};

/**
 * Debug group function for grouping related debug logs
 * @param {string} label - Group label
 * @param {Function} callback - Function containing debug logs to group
 */
export const debugGroup = (label, callback) => {
  if (isDebugEnabled()) {
    console.group(`[RESPONZA DEBUG] ${label}`);
    callback();
    console.groupEnd();
  }
};

/**
 * Debug time function for measuring execution time
 * @param {string} label - Timer label
 * @returns {Function} Function to call when timing should end
 */
export const debugTime = (label) => {
  if (isDebugEnabled()) {
    const timerLabel = `[RESPONZA DEBUG TIMER] ${label}`;
    console.time(timerLabel);
    return () => console.timeEnd(timerLabel);
  }
  return () => {}; // No-op function if debug is disabled
};

// Export a default debug object with all methods
export default {
  log: debug,
  error: debugError,
  warn: debugWarn,
  info: debugInfo,
  table: debugTable,
  group: debugGroup,
  time: debugTime,
  isEnabled: isDebugEnabled,
};